import React from 'react';
import { capitalize } from 'lodash';

import { ColorPalette } from 'types/constants';

import SquareIcon from './square_icon';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  colorPalette?: ColorPalette;
  fallbackLetter?: string;
  name?: string;
  size?: number;
  subject?: string | null;
}

const MATH_SUBJECTS = ['math', 'quantitative_reasoning'];
const VERBAL_SUBJECTS = ['english', 'reading', 'reading_and_writing', 'verbal', 'verbal_reasoning'];
const DATA_SUBJECTS = ['data', 'data_insights'];

// Section names that unambiguously imply a subject, for sections whose subject attribute
// is unset — a section called "Listening" means the same thing on every exam.
const NAME_SUBJECTS = ['listening', 'math', 'reading', 'science', 'speaking', 'verbal', 'writing'];

const subjectFromName = (name?: string): string | null => {
  const normalized = (name || '').trim().toLowerCase();
  return NAME_SUBJECTS.indexOf(normalized) !== -1 ? normalized : null;
};

export const ExamSectionIcon: React.FC<Props> = ({
  colorPalette,
  fallbackLetter,
  name,
  size = 48,
  subject: subjectProp
}) => {
  const palette = colorPalette || { dark: colors.purple, light: colors.purpleLight };
  const subject = subjectProp || subjectFromName(name);

  if (subject && MATH_SUBJECTS.indexOf(subject) !== -1) {
    return <SquareIcon colorPalette={palette} iconType="math_operations" size={size} />;
  } else if (subject && VERBAL_SUBJECTS.indexOf(subject) !== -1) {
    return <SquareIcon colorPalette={palette} iconType="notebook" size={size} />;
  } else if (subject && DATA_SUBJECTS.indexOf(subject) !== -1) {
    return <SquareIcon colorPalette={palette} iconContents="bar-chart" iconType="fa" size={size} />;
  } else if (subject === 'listening') {
    return <SquareIcon colorPalette={palette} iconContents="headphones" iconType="fa" size={size} />;
  } else if (subject === 'science') {
    return <SquareIcon colorPalette={palette} iconContents="flask" iconType="fa" size={size} />;
  } else if (subject === 'speaking') {
    return <SquareIcon colorPalette={palette} iconContents="commenting-o" iconType="fa" size={size} />;
  } else if (subject === 'writing') {
    return <SquareIcon colorPalette={palette} iconContents="edit" iconType="fa" size={size} />;
  }

  const letter = fallbackLetter || (subject ? capitalize(subject[0]) : '?');
  return <SquareIcon colorPalette={palette} iconContents={letter} iconType="letter" size={size} />;
};

export default ExamSectionIcon;
